import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { RevisionAuditBadge } from 'features/knowledgeDocumentViewer/components/metadata/RevisionAuditBadge'
import { RevisionFamilyDropdown } from 'features/knowledgeDocumentViewer/components/metadata/RevisionFamilyDropdown'
import { RevisionCompareLauncher } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionCompareLauncher'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePageRevisionFamily } from 'features/knowledgeDocumentViewer/hooks/usePageRevisionFamily'
import { useRevisionHistoryForDocument } from 'features/knowledgeDocumentViewer/hooks/useRevisionHistory'
import { useSystemDocumentPageByNum } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentPageByNum'
import { useSystemDocumentByBqId } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import { getSystemDocumentBqId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  revisionFamilyHasNewerRevision,
  revisionFamilyMemberMenuItem,
  type RevisionFamilyMemberView,
} from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import { resolveRevisionAudit } from 'features/knowledgeDocumentViewer/utils/revisionAuditModel'
import { formatRevisionLabel } from 'features/knowledgeDocumentViewer/utils/revisionHelpers'
import { useCallback, useMemo } from 'react'

export function DocumentHeaderRevisionChip() {
  const { t } = useLingui()
  const {
    isOnDocument,
    isOnLinkedDocuments,
    currentDocumentId,
    currentPageNumber,
    handleSelectDocument,
  } = useKnowledgeDocumentUXContext()
  const enabled =
    isOnDocument && !isOnLinkedDocuments && Boolean(currentDocumentId)
  const { document } = useSystemDocumentByBqId(
    enabled ? currentDocumentId : undefined
  )
  const bqDocumentId = document ? getSystemDocumentBqId(document) : undefined
  const { page: pageEntry } = useSystemDocumentPageByNum({
    systemDocumentEntryId: document?.entry.id,
    bqDocumentId,
    pageNum: currentPageNumber,
    enabled,
  })
  const { members, canonicalRevisionModel, hasRevisionFamily } =
    usePageRevisionFamily({
      pageEntry,
      pageUserEdit: undefined,
      currentBqDocumentId: bqDocumentId,
      currentPageNum: currentPageNumber,
      enabled,
    })
  const current = members.find((member) => member.isCurrent) ?? members[0]
  const { siteID } = useKnowledgeDocumentDataContext()
  const { auditState, finding } = useRevisionHistoryForDocument({
    siteId: siteID,
    drawingNumber: pageEntry?.entry.data.issuer_drawing_number ?? undefined,
    sheetNumber: pageEntry?.entry.data.sheet_number ?? undefined,
    enabled,
  })
  const onOpenDocument = useCallback(
    (documentId: string, pageNum: number | undefined) => {
      handleSelectDocument(documentId, undefined, undefined, undefined, pageNum)
    },
    [handleSelectDocument]
  )
  const audit = useMemo(
    () => resolveRevisionAudit(auditState, finding, members),
    [auditState, finding, members]
  )
  const auditBadge = audit ? (
    <RevisionAuditBadge
      size='lg'
      audit={audit}
      onOpenDocument={onOpenDocument}
    />
  ) : null

  const handleSelect = useCallback(
    (member: RevisionFamilyMemberView) => {
      handleSelectDocument(
        member.bqDocumentId,
        undefined,
        undefined,
        undefined,
        member.pageNum
      )
    },
    [handleSelectDocument]
  )

  if (!hasRevisionFamily || !current) {
    return auditBadge ? (
      <Box shrink={false} display='flex' gap='sm'>
        {auditBadge}
      </Box>
    ) : null
  }

  return (
    <Box shrink={false} display='flex' gap='sm'>
      <RevisionFamilyDropdown
        revisions={members}
        toMenuItem={(member) => revisionFamilyMemberMenuItem(member, true)}
        isOutdated={revisionFamilyHasNewerRevision(members)}
        triggerLabel={formatRevisionLabel(current.revisionCode)}
        outdatedTooltip={t`This revision is out of date`}
        size='lg'
        onSelectRevision={handleSelect}
      />
      {auditBadge}
      {enabled ? (
        <RevisionCompareLauncher
          bqDocumentId={current.bqDocumentId}
          pageNum={current.pageNum}
          canonicalRevisionModel={canonicalRevisionModel}
        />
      ) : null}
    </Box>
  )
}
